import {isAccountingAccount, isAccountingStatementDetail, isAccountingTransfer, uniqueAccountingRows, safeAccountingRecord} from './accounting-read-contracts.js';
import {isPrivateCommunityReport, isPublicCommunityReport} from './community-report-contracts.js';
import {isAssemblyOverviewReport} from './treasury-overview-contracts.js';
const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isDate = value => typeof value === 'string' && Number.isFinite(Date.parse(value));
const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

export class AccountingStatementResponseContractError extends Error {
  constructor(detail) {
    super(`Invalid Treasury statement response: ${detail}`);
    this.name = 'AccountingStatementResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}
export class AccountingRecoveryResponseContractError extends Error {
  constructor() {
    super('The earlier action could not be confirmed. Please check again.');
    this.name = 'AccountingRecoveryResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const reallocationRecord = isAccountingTransfer;
const statementDetail = value => isAccountingStatementDetail(value?.data) && typeof value.transactionsError === 'string'
  && Array.isArray(value.transactions) && value.transactions.length <= 500 && uniqueAccountingRows(value.transactions) && value.transactions.every(row => safeAccountingRecord(row) && typeof row.id === 'string' && row.id.length > 0
    && isFiniteNumber(row.amount) && typeof row.fundId === 'string' && isDate(row.date) && typeof row.reference === 'string'
    && (row.matchedStatement == null || typeof row.matchedStatement === 'string'))
  && Array.isArray(value.advanceSettlements) && value.advanceSettlements.length <= 500 && uniqueAccountingRows(value.advanceSettlements) && value.advanceSettlements.every(row => safeAccountingRecord(row) && typeof row.id === 'string' && row.id.trim()
    && typeof row.fundId === 'string' && row.fundId.trim() && isDate(row.date) && isFiniteNumber(row.amount) && row.amount >= 0)
  && Array.isArray(value.reallocations) && value.reallocations.length <= 500 && uniqueAccountingRows(value.reallocations) && value.reallocations.every(reallocationRecord);

export const readStatementDetailResponse = payload => {
  if (!statementDetail(payload)) throw new AccountingStatementResponseContractError('expected a statement with transaction, settlement and transfer detail lists.');
  return payload;
};

const accountingActionResources = {
  create_account: 'account',
  create_statement: 'statement', match: 'statement', unmatch: 'statement', close_statement: 'statement', cancel_statement: 'statement',
  create_report: 'report', revise_report: 'report', review_report: 'report', publish_report: 'report', withdraw_report: 'report', capture_report_planning: 'report', remove_report_planning: 'report',
  propose_reallocation: 'reallocation', approve_reallocation: 'reallocation', cancel_reallocation: 'reallocation',
};
const reportRecord = data => isRecord(data) && (Object.hasOwn(data, 'statement') ? isPrivateCommunityReport(data) : isPublicCommunityReport(data) && isAssemblyOverviewReport(data));
export const readAccountingActionResponse = (action, payload) => {
  const resource = accountingActionResources[action];
  const data = payload?.data;
  const validRecord = isRecord(data) && typeof data.id === 'string' && data.id.length > 0;
  const validResource = resource === 'account' ? validRecord && isAccountingAccount(data)
    : resource === 'statement' ? validRecord && isAccountingStatementDetail(data)
      : resource === 'report' ? validRecord && reportRecord(data)
        : resource === 'reallocation' ? validRecord && reallocationRecord(data) : false;
  if (!validResource || data.replayed !== undefined && typeof data.replayed !== 'boolean') throw new AccountingActionResponseContractError(action);
  return payload;
};
export class AccountingActionResponseContractError extends Error {
  constructor(action) {
    super(`Invalid Treasury accounting response for ${action}.`);
    this.name = 'AccountingActionResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

export const readAccountingRecoveryResponse = payload => {
  if (!isRecord(payload) || !['recorded', 'not_recorded'].includes(payload.outcome)) throw new AccountingRecoveryResponseContractError();
  if (payload.outcome === 'not_recorded') {
    if (payload.resource !== null || payload.data !== null) throw new AccountingRecoveryResponseContractError();
    return payload;
  }
  if (payload.data === null) {
    if (payload.resource !== null) throw new AccountingRecoveryResponseContractError();
    return payload;
  }
  if (!['account', 'statement', 'report', 'reallocation'].includes(payload.resource)) throw new AccountingRecoveryResponseContractError();
  if (payload.resource === 'statement') {
    if (!statementDetail(payload)) throw new AccountingRecoveryResponseContractError();
    return payload;
  }
  const data = payload.data;
  const hasId = isRecord(data) && typeof data.id === 'string' && data.id.length > 0;
  if (!hasId) throw new AccountingRecoveryResponseContractError();
  if (payload.resource === 'reallocation' && (!reallocationRecord(data)
    || !Object.hasOwn(data, 'reversal') || !(data.reversal === null || safeAccountingRecord(data.reversal) && typeof data.reversal.id === 'string' && data.reversal.id.trim()
      && ['proposed', 'posted', 'cancelled'].includes(data.reversal.status)))) throw new AccountingRecoveryResponseContractError();
  if (payload.resource === 'account' && !isAccountingAccount(data)) throw new AccountingRecoveryResponseContractError();
  if (payload.resource === 'report' && !reportRecord(data)) throw new AccountingRecoveryResponseContractError();
  return payload;
};
